import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Record, List } from 'immutable';
import * as localise from '../../../utils/localise';
import { InPlayFlag, thirdPartyFlag, bonusBetFlag, rule4DeductionFlag } from '../BetReportIcons/BetReportIcons';
import './SelectionRow.scss';

const BASE = 'mb-reportSelectionRow';

const Badge = ({ count, onToggle, openClass }) => (
  <a
    role="button"
    tabIndex={0}
    onClick={() => onToggle()}
    className={`${BASE}__toggle ${openClass}`}
  >
    {count}
  </a>
);

Badge.propTypes = {
  openClass: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired
};


const SelectionRow = ({ selection, bets, offers, type, onToggle, currency, oddsType, isOpen }) => {
  const isSingleBet = bets.size === 1 || offers.size === 1;
  const count = type === 'offers' ? offers.size : bets.size;

  let matchedTime = '---';
  let betId = '\u00A0';
  let odds = selection.odds;
  let adjusted;
  let stake = selection.stake;
  let grossPnl = selection.profitAndLoss;
  let netPnl = selection.profitAndLoss - (selection.commission || 0);
  const pnlClass = netPnl > 0 ? 'Positive' : 'Negative';
  let useBonus = selection.useBonus;
  let commission = selection.commission !== null ? selection.commission : '-.--';
  // let originator = selection.originator;
  let inPlay = selection.inPlay;
  let rate = selection.commissionRate !== null ? selection.commissionRate : null;
  let liability = selection.liability;
  let thirdPartyClientId = '';
  let noCursorClass = '';
  let toggleElement = (
    <Badge
      onToggle={onToggle}
      count={count}
      openClass={isOpen ? `${BASE}__toggle--open` : ''}
    />
  );

  if (count < 2) {
    noCursorClass = `${BASE}--noCursor`;
  }

  if (isSingleBet) {
    const bet = bets.get(0) || offers.get(0);
    if (type === 'bets') {
      matchedTime = moment(bet.matchedTime).format('DD MMM YYYY HH:mm');
    } else if (type === 'offers') {
      matchedTime = moment(bet.submittedTime).format('DD MMM YYYY HH:mm');
    } else if (type === 'settled') {
      matchedTime = moment(bet.matchedTime).format('DD MMM YYYY HH:mm');
    }

    betId = bet.id;
    odds = bet.odds;
    stake = bet.stake;
    liability = bet.liability;
    grossPnl = bet.profitAndLoss;
    netPnl = bet.profitAndLoss - (bet.commission || 0);
    commission = bet.commission !== null ? bet.commission : '-.--';
    // originator = bet.originator;
    rate = bet.commissionRate !== null ? bet.commissionRate : null;
    adjusted = bet.adjusted;
    inPlay = bet.inPlay;
    thirdPartyClientId = bet.thirdPartyClientId;
    toggleElement = null;
    useBonus = bet.useBonus;
  }

  let grossPnlValue = liability ? '\u00A0' : localise.currency(grossPnl, currency);

  if (netPnl === grossPnl) {
    grossPnlValue = '-.--';
  }

  const layBonus = selection.side.toLowerCase() === 'lay' && useBonus;
  const backBonus = selection.side.toLowerCase() === 'back' && useBonus;

  return (
    <tr
      className={`${BASE} ${noCursorClass}`}
      role="button"
      tabIndex={0}
      onClick={() => { if (toggleElement) onToggle(selection.id); }}
    >
      <td className={`${BASE}__column1`}>
        {toggleElement}
      </td>
      <td>
        <span className={`${BASE}__selectionName  ${BASE}__selectionName--${selection.side.toLowerCase()}`}>
          {selection.name}
        </span>
        <span className={`${BASE}__side`}>
          {_.capitalize(selection.side.toLowerCase())}
        </span>
      </td>
      <td className={`${BASE}__matchedTimeCell`}>
        <div>
          <span className={`${BASE}__matchedTime`}>
            {matchedTime}
          </span>
          <span className={`${BASE}__betId`}>
            {betId}
          </span>
        </div>
        {
          isSingleBet &&
          (
            <div className={`${BASE}__thirdPartyLogo`}>
              {inPlay ? <InPlayFlag type={type} /> : null}
              {type !== 'offers' && thirdPartyClientId ? thirdPartyFlag : null}
              {layBonus && type !== 'settled' ? bonusBetFlag : null}
              {backBonus || (useBonus && type === 'settled') ? bonusBetFlag : null}
              {adjusted ? rule4DeductionFlag : null}
            </div>
          )
        }
      </td>
      <td className={`${BASE}__odds`}>
        {localise.odds(odds, { oddsType, side: selection.side.toLowerCase() })}
        {
          /*
          // Hide originator flag on Settled Bets
          // This is a temporary solution until the originator flag is available for Current Bets
          originator
            ? <div className={`${BASE}__originatorIcon`}>
              <IconStar />
            </div>
            : null
          */
        }
      </td>
      <td className={`${BASE}__stake`}>
        {localise.currency((stake), currency)}
      </td>
      <td className={`${BASE}__pnl`}>
        <span>
          <span className={`${BASE}__netPnl ${BASE}__netPnl--${type}${pnlClass}`}>
            {localise.currency((netPnl || liability), currency)}
          </span>
          <span className={`${BASE}__grossPnl`}>
            {grossPnlValue}
          </span>
        </span>
      </td>
      <td className={`${BASE}__commissions`}>
        <span className={`${BASE}__commAmount ${BASE}__commAmount--${type}`}>
          {
            commission !== '-.--'
              ? localise.currency(commission || selection.potentialProfit, currency)
              : '-.--'
          }
        </span>
        <span className={`${BASE}__commType`}>
          {rate ? `${rate}%` : ''}
        </span>
      </td>
    </tr>
  );
};

SelectionRow.propTypes = {
  type: PropTypes.oneOf(['bets', 'offers', 'settled']).isRequired,
  currency: PropTypes.string,
  oddsType: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  selection: PropTypes.instanceOf(Record).isRequired,
  bets: PropTypes.instanceOf(List),
  offers: PropTypes.instanceOf(List),
  isOpen: PropTypes.bool
};

SelectionRow.defaultProps = {
  bets: new List(),
  offers: new List()
};

export default SelectionRow;
